import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import {
  AlertTriangle,
  Lock,
  User,
  Calendar,
  Settings,
  Wrench,
  Loader2,
  CheckCircle,
  Search,
  MapPin,
  Users,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { 
  usePriority, 
  useReworkOrder,
  useFieldForceRecommend
} from "@features/wx/dispatcher/hooks";
import { toast } from "@shared/ui/use-toast";

interface CreateReworkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentOrder: {
    id: string;
    name: string;
    rejectionReason: string;
    rejectionReasonDisplay:string;
    rejectionComments: string;
    rejectedBy: string;
    serviceOrderData?: any;
  };
}

// Helper function to extract coordinates from service order
const extractMeterLocations = (serviceOrder: any): string[] => {
  if (!serviceOrder) return [];

  const coordinates = [];

  if (serviceOrder.latitude && serviceOrder.longitude) {
    coordinates.push(`(${serviceOrder.latitude}, ${serviceOrder.longitude})`);
  }

  if (serviceOrder.location?.lat && serviceOrder.location?.lng) {
    coordinates.push(
      `(${serviceOrder.location.lat}, ${serviceOrder.location.lng})`
    );
  }

  if (serviceOrder.meter?.coordinates) {
    const { lat, lng } = serviceOrder.meter.coordinates;
    if (lat && lng) {
      coordinates.push(`(${lat}, ${lng})`);
    }
  }

  return coordinates;
};

// Transform API data to match UI expectations
const transformTechnicianData = (technicians: any[]) => {
  return technicians.map((tech) => ({
    id: tech.fieldForceId,
    name: tech.name,
    workload: tech.workLoad || 0,
    distance: tech.distance,
    skills: tech.skill || [],
    skillMatch: tech.skillMatch,
    status:
      tech.availabilityScore >= 100
        ? "available"
        : tech.workLoad >= 80
        ? "overloaded"
        : "busy",
    utilization: tech.workLoad || 0,
    rating: tech.rating || 0,
    role: tech.roleName,
    originalData: tech,
  }));
};

const CreateReworkOrderModal = ({
  isOpen,
  onClose,
  parentOrder,
}: CreateReworkOrderModalProps) => {
  const navigate = useNavigate();
  const { data: priorityData } = usePriority();
  const { mutateAsync: createReworkOrder, isPending } = useReworkOrder();
  
  const [searchData, setSearchData] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const serviceData = parentOrder.serviceOrderData || {};

  // Extract meter locations and SOP ID
  const meterLocations = useMemo(() => {
    return extractMeterLocations(serviceData);
  }, [serviceData]);

  const sopId = serviceData?.sop || serviceData?.sopId || "";

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
      setCurrentPage(1);
    }
  }, [isOpen]);

  // Debounced search
  const debouncedSearchFn = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchData(searchTerm);
        setCurrentPage(1);
      }, 500);
    };
  }, []);

  // API call for field force recommendations
  const {
    data: recommendationData,
    isLoading: loadingTechnicians,
    error: technicianError,
    refetch: refetchTechnicians,
  } = useFieldForceRecommend(
    {
      sopId: sopId.toString(),
      meterLocations: meterLocations,
      searchData: searchData || "",
      page: currentPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!sopId && isOpen,
    }
  );

  const totalCount = recommendationData?.count || 0;

  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return {
      currentPage,
      totalPages,
      totalItems: totalCount,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [totalCount, currentPage, itemsPerPage]);

  const technicians = recommendationData?.results
    ? transformTechnicianData(recommendationData.results)
    : [];

  const priorityOptions =
    priorityData?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  // Form fields - WITHOUT technician dropdown
  const formFields: FormField[] = [
    {
      name: "priority",
      label: "Priority Level",
      type: "select",
      required: true,
      className: "col-span-2",
      options: priorityOptions,
      helperText: "Set the priority level for this rework order",
    },
    {
      name: "expectedStartDate",
      label: "Expected Start Date",
      type: "datepicker",
      required: false,
      allowPast: false,
      helperText: "When should the technician start working on this rework?",
    },
    {
      name: "expectedCompletionDate",
      label: "Expected Completion Date",
      type: "datepicker",
      required: false,
      allowPast: false,
      helperText: "Expected completion date for this rework order",
    },
    {
      name: "notes",
      label: "Notes for Technician",
      type: "textarea",
      required: false,
      className: "col-span-2",
      rows: 4,
      placeholder:
        "Add specific instructions for the rework. This is in addition to the reviewer's comments.",
      helperText:
        "Provide any additional instructions or context for the technician",
      maxLength: 1000,
    },
  ];

  const formConfig: FormConfig = {
    fields: formFields,
    showSubmitButton: false,
    showResetButton: false,
    layout: "vertical",
    spacing: "medium",
    classes: FIELD_CLASSES,
  };

  const form = FormService.createForm(formConfig);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("");
  };

  const getWorkloadColor = (workload: number) => {
    if (workload === 0) return "text-green-600";
    if (workload >= 4) return "text-red-600";
    return "text-orange-600";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-700";
      case "busy":
        return "bg-yellow-100 text-yellow-700";
      case "overloaded":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleAutoAssign = () => {
    if (technicians.length === 0) return;

    const availableTechnicians = technicians.filter(
      (tech) => tech.status === "available"
    );

    if (availableTechnicians.length === 0) {
      toast({
        title: "No Available Technicians",
        description: "All technicians are currently busy or overloaded.",
        variant: "destructive",
      });
      return;
    }

    const bestMatch = availableTechnicians.reduce((best: any, current: any) => {
      if (current.workload < best.workload) return current;
      if (
        current.workload === best.workload &&
        parseFloat(current.distance) < parseFloat(best.distance)
      ) {
        return current;
      }
      return best;
    });

    setSelectedTechnician(bestMatch);
    
    toast({
      title: "Auto Assign Complete",
      description: `Assigned to ${bestMatch.name}`,
    });
  };

  const handleParentOrderClick = () => {
    navigate(`/service-orders/dispatcher/rejected-service-order/${parentOrder.id}`);
    onClose();
  };

  const handleCreateRework = async (formData: any) => {
    if (!selectedTechnician) {
      toast({
        title: "No Technician Selected",
        description: "Please select a technician before creating the rework order.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload = {
        technician_id: parseInt(selectedTechnician.id),
        priority: parseInt(formData.priority),
        notes: formData.notes || "",
        expected_start_date: formData.expectedStartDate,
        expected_end_date: formData.expectedCompletionDate
      };

       if (formData.expectedStartDate && formData.expectedCompletionDate) {
    const startDate = new Date(formData.expectedStartDate);
    const endDate = new Date(formData.expectedCompletionDate);
    
    if (startDate > endDate) {
      toast({
        title: "Invalid Date Range",
        description: "Expected start date cannot be after the expected completion date.",
        variant: "destructive",
      });
      return;
    }
  }
      await createReworkOrder({
        id: parentOrder.id,
        payload,
      });

      toast({
        title: "Success",
        description: "Rework order created successfully",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create rework order",
        variant: "destructive",
      });
    }
  };

  const handleRetry = () => {
    refetchTechnicians();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "bg-red-100 text-red-800";
      case "High":
        return "bg-orange-100 text-orange-800";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "Low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const watchedPriority = form.watch("priority");

  const CustomFooter = () => {
    return (
      <DialogFooter className="mt-6 flex gap-3">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={form.handleSubmit(handleCreateRework)}
          disabled={!selectedTechnician || isPending}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Create Rework Order"
          )}
        </Button>
      </DialogFooter>
    );
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Create Rework Service Order
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Context Banner */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-800 mb-2">
                  Rework for Rejected Order:{" "}
                  <button
                    onClick={handleParentOrderClick}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {serviceData.jobCardNo}
                  </button>
                </h4>
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="font-medium text-red-800">
                      Rejection Reason:
                    </span>{" "}
                    <span className="text-red-700">
                      {parentOrder?.serviceOrderData?.rejectionReasonDisplay}
                    </span>
                  </div>  
                  <div>
                    <span className="font-medium text-red-800">
                      Reviewer Comments:
                    </span>{" "}
                    <span className="text-red-700">
                      "{parentOrder.rejectionComments}"
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-red-800">
                      Rejected By:
                    </span>{" "}
                    <span className="text-red-700">
                      {parentOrder.rejectedBy}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pre-defined & Locked Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* SOP Template */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Standard Operating Procedure (SOP)
                  <Lock className="h-4 w-4 text-gray-500" />
                </CardTitle>
              </CardHeader>
              <CardContent className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="bg-blue-600 text-white rounded-md p-2 flex items-center justify-center">
                    <span className="text-sm font-bold">
                      {serviceData.sopShortName?.charAt(0) || "S"}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {serviceData.sopName || "N/A"}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {serviceData.sopShortName || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                  <div className="flex items-center gap-1">
                    <Wrench className="h-4 w-4 text-gray-400" />
                    <span>{serviceData.sopAssociationTypeDisplay || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>TAT: {serviceData.slaStatus || "N/A"}</span>
                  </div>
                </div>
                {serviceData.sopUtilityService &&
                  serviceData.sopUtilityService.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-800 mb-3">
                      {serviceData.sopUtilityService[0]}
                    </Badge>
                  )}
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Job Card: </span>
                    {serviceData.jobCardNo || "N/A"}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Operation Type: </span>
                    {serviceData.sopOperationTypeDisplay || "N/A"}
                  </div>
                </div>
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <Lock className="h-4 w-4 inline mr-1" />
                  This SOP is locked as it is part of a rework order.
                </div>
              </CardContent>
            </Card>

            {/* Service Order Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Service Order Details
                  <Lock className="h-4 w-4 text-gray-500" />
                </CardTitle>
              </CardHeader>
              <CardContent className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Service Number
                      </label>
                      <p className="text-blue-600 font-medium">
                        {serviceData.serviceNo || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Job Card No
                      </label>
                      <p className="font-medium">
                        {serviceData.jobCardNo || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Account Number
                      </label>
                      <p className="font-medium">
                        {serviceData.requestAccountNo || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Status
                      </label>
                      <p className="font-medium">
                        {serviceData.statusDisplay || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Created Date
                      </label>
                      <p className="font-medium">
                        {serviceData.createdDate || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Completed Date
                      </label>
                      <p className="font-medium">
                        {serviceData.completedDate || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Area
                      </label>
                      <p className="font-medium">{serviceData.areaName || "N/A"}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Sub Area
                      </label>
                      <p className="font-medium">
                        {serviceData.subAreaName || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <Lock className="h-4 w-4 inline mr-1" />
                  This service order information is locked and cannot be changed.
                </div>
              </CardContent>
            </Card>
          </div>

          {/* NEW: Select Technician Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                Select Technician
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAutoAssign}
                disabled={loadingTechnicians || technicians.length === 0}
                className="flex items-center gap-2"
                type="button"
              >
                {loadingTechnicians ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4" />
                    Auto Assign
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, ID, or skills..."
                    value={searchInputValue}
                    onChange={(e) => {
                      setSearchInputValue(e.target.value);
                      debouncedSearchFn(e.target.value);
                    }}
                    className="pl-10 pr-4 py-2"
                    disabled={loadingTechnicians}
                  />
                  {loadingTechnicians && (
                    <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 animate-spin" />
                  )}
                </div>

                {/* Selected Technician Preview */}
                {selectedTechnician && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-green-800 mb-2">
                          Selected Technician
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium text-green-800">Name:</span>{" "}
                            <span className="text-green-700">
                              {selectedTechnician.name} ({selectedTechnician.role})
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-green-800">Workload:</span>{" "}
                            <span className="text-green-700">
                              {selectedTechnician.workload} tasks
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-green-800">Distance:</span>{" "}
                            <span className="text-green-700">
                              {selectedTechnician.distance}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Technicians List */}
                <div className="border rounded-lg">
                  {technicianError ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="text-center text-red-600">
                        <Users className="h-8 w-8 mx-auto mb-2 text-red-300" />
                        <p className="text-sm mb-2">Failed to load technicians</p>
                        <Button variant="outline" size="sm" onClick={handleRetry}>
                          Try Again
                        </Button>
                      </div>
                    </div>
                  ) : technicians.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="text-center text-gray-500">
                        <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">
                          {searchData
                            ? `No technicians found matching "${searchData}"`
                            : "No technicians available"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto divide-y">
                      {technicians.map((technician: any) => (
                        <div
                          key={technician.id}
                          className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                            selectedTechnician?.id === technician.id
                              ? "bg-blue-50 border-l-4 border-blue-500"
                              : ""
                          }`}
                          onClick={() => setSelectedTechnician(technician)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                                {getInitials(technician.name)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {technician.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {technician.role}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <Badge className={getStatusColor(technician.status)}>
                                {technician.status}
                              </Badge>
                              <div className="flex items-center gap-1 text-gray-500">
                                <MapPin className="h-3 w-3" />
                                <span className="text-xs font-medium">
                                  {technician.distance}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3 text-gray-500" />
                              <span className="text-xs text-gray-500">
                                Workload:{" "}
                              </span>
                              <span
                                className={`font-medium text-xs ${getWorkloadColor(
                                  technician.workload
                                )}`}
                              >
                                {technician.workload}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">
                                Rating:{" "}
                              </span>
                              <span className="text-xs font-medium text-yellow-600">
                                ★ {technician.rating.toFixed(1)}
                              </span>
                            </div>
                          </div>

                          <div>
                            <div className="flex items-center gap-1 mb-1">
                              <Zap className="h-3 w-3 text-gray-500" />
                              <p className="text-xs text-gray-500">Skills:</p>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {technician.skills
                                .slice(0, 3)
                                .map((skill: string) => (
                                  <Badge
                                    key={skill}
                                    variant="outline"
                                    className="text-xs px-2 py-0"
                                  >
                                    {skill}
                                  </Badge>
                                ))}
                              {technician.skills.length > 3 && (
                                <Badge
                                  variant="outline"
                                  className="text-xs px-2 py-0"
                                >
                                  +{technician.skills.length - 3}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>

                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        const startPage = Math.max(1, currentPage - 2);
                        const endPage = Math.min(pagination.totalPages, startPage + 4);
                        const adjustedStartPage = Math.max(1, endPage - 4);
                        const page = adjustedStartPage + i;

                        if (page > pagination.totalPages) return null;

                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={`w-8 h-8 p-0 ${
                              currentPage === page
                                ? "bg-blue-600 text-white"
                                : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </Button>
                        );
                      }
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))
                      }
                      disabled={currentPage === pagination.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Dynamic Form Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                Assignment & Priority
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Live Preview Section */}
                {(selectedTechnician || watchedPriority) && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">
                      Assignment Preview
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedTechnician && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">
                            Assigned Technician:
                          </span>
                          <span className="font-medium text-gray-800">
                            {selectedTechnician.name}
                          </span>
                        </div>
                      )}
                      {watchedPriority && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Priority Level:</span>
                          <Badge className={getPriorityColor(watchedPriority)}>
                            {priorityOptions.find(p => p.value === watchedPriority)?.label || watchedPriority} Priority
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Dynamic Form */}
                <DynamicForm
                  fields={formFields}
                  form={form}
                  gridColumns={2}
                  config={formConfig}
                  responsiveColumns={{
                    md: 2,
                  }}
                  renderCustomFooter={CustomFooter}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateReworkOrderModal;